import { useEffect, type Dispatch, type MutableRefObject, type RefObject, type SetStateAction } from 'react'
import type { EnglishTestQuestionDto } from '@/api/generated/models'
import { buildInitialAssessmentState } from './answerReview'
import type { EnglishTestModalProps, EnglishTestAnswerReview, QuestionAnswerValue } from './types'

type UseEnglishTestModalEffectsArgs = Pick<
  EnglishTestModalProps,
  'initialAnswers' | 'isOpen'
> & {
  currentGroupIndex: number
  questions: EnglishTestQuestionDto[]
  questionsContainerRef: RefObject<HTMLDivElement | null>
  setAnswers: Dispatch<SetStateAction<QuestionAnswerValue[]>>
  setCurrentGroupIndex: Dispatch<SetStateAction<number>>
  setSubmissionError: (error: string | null) => void
  setSubmittedAnswers: Dispatch<SetStateAction<Record<number, EnglishTestAnswerReview>>>
  storedEmailValue: string
  submittedTextAnswersRef: MutableRefObject<Record<number, string>>
}

export function useEnglishTestModalEffects({
  currentGroupIndex,
  initialAnswers = [],
  isOpen,
  questions,
  questionsContainerRef,
  setAnswers,
  setCurrentGroupIndex,
  setSubmissionError,
  setSubmittedAnswers,
  storedEmailValue,
  submittedTextAnswersRef
}: UseEnglishTestModalEffectsArgs) {
  useEffect(() => {
    if (!isOpen) {
      setCurrentGroupIndex(0)
      setAnswers([])
      setSubmittedAnswers({})
      submittedTextAnswersRef.current = {}
      setSubmissionError(null)
    }
  }, [
    isOpen,
    setAnswers,
    setCurrentGroupIndex,
    setSubmissionError,
    setSubmittedAnswers,
    submittedTextAnswersRef
  ])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    if (questions.length === 0) {
      setAnswers([])
      setSubmittedAnswers({})
      return
    }

    const nextState = buildInitialAssessmentState(questions, initialAnswers, storedEmailValue)

    setAnswers(nextState.answers)
    setSubmittedAnswers(nextState.submittedAnswers)
    submittedTextAnswersRef.current = nextState.submittedTextAnswers
  }, [
    initialAnswers,
    isOpen,
    questions,
    setAnswers,
    setSubmittedAnswers,
    storedEmailValue,
    submittedTextAnswersRef
  ])

  useEffect(() => {
    questionsContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })

    if (typeof window !== 'undefined') {
      const containerTop = questionsContainerRef.current?.getBoundingClientRect().top

      if (typeof containerTop === 'number') {
        window.scrollTo({
          top: Math.max(window.scrollY + containerTop - 24, 0),
          behavior: 'smooth'
        })
      }
    }
  }, [currentGroupIndex, questionsContainerRef])
}
